import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';

const SENDER_ADDRESS = 'noreply@collectionkeeper.org';
const RESEND_COOLDOWN_S = 60;

// Proof row is computed from the registry so it can never go stale.
const SET_COUNT = SET_REGISTRY.length;
const CARD_COUNT = SET_REGISTRY.reduce((n, s) => n + (s.totalCards || 0), 0);
const PROOF_LINE = `${SET_COUNT} CHECKLISTS · ${Math.floor(CARD_COUNT / 1000).toLocaleString()},000+ CARDS`;

// Returning collectors get the sign-in tab; everyone else lands on CREATE ACCOUNT,
// because every collector without an account is seeing this screen for the first time.
const SEEN_KEY = 'ck_has_signed_in';
const hasSignedInBefore = () => {
  try { return localStorage.getItem(SEEN_KEY) === '1'; } catch (e) { return false; }
};
const markSignedIn = () => {
  try { localStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* private mode — not worth failing over */ }
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Supabase's email enumeration protection (on for CK) makes signUp with an
// address that already has an account return SUCCESS, not an error: a fabricated
// user object with no session and — the only tell — an empty `identities` array.
// A genuine signup always comes back with its email identity attached, verified
// against both projects: every user row has exactly one identity, none have zero.
//
// Fails safe. If `identities` is ever absent rather than empty, this returns
// false and the collector gets the normal CHECK YOUR EMAIL panel, which is the
// behaviour we had before. The reverse mistake — telling a genuinely new
// collector they already have an account — would block activation outright.
const isObfuscatedExistingUser = (data) =>
  !!data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0;

export default function AuthPage() {
  const [mode, setMode] = useState(hasSignedInBefore() ? 'login' : 'signup'); // 'login' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [capsLock, setCapsLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // {type: 'error'|'success', text, action}
  // Once a message has been shown we keep its slot reserved, so later messages
  // never shove the submit button out from under the collector's thumb.
  const [slotReserved, setSlotReserved] = useState(false);
  // Non-null after a signup (or an unconfirmed sign-in attempt): the address we
  // sent the confirmation link to. Swaps the form for the CHECK YOUR EMAIL panel.
  const [pendingEmail, setPendingEmail] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const say = (type, text, action) => {
    setMessage({ type, text, action });
    setSlotReserved(true);
  };

  // The failure collectors actually hit: they have an account and picked the
  // wrong tab. Move them across with the email already filled.
  const alreadyRegistered = () => {
    setMode('login');
    setPassword('');
    say('success', 'You already have an account. Enter your password to sign in.');
  };

  const switchMode = (next) => {
    setMode(next);
    setMessage(null);
    setEmailError(null);
  };

  const validateEmail = () => {
    if (email && !EMAIL_RE.test(email)) {
      setEmailError('Enter a valid email address, like name@example.com');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    if (!validateEmail()) return;
    setLoading(true);
    setMessage(null);
    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          // Signed up but never confirmed — route them to the resend panel
          // instead of dead-ending on an error message.
          if (/not confirmed/i.test(error.message)) {
            setPendingEmail(email);
            setResendCooldown(0);
          } else if (/invalid login credentials/i.test(error.message)) {
            // Could be a wrong password or no account at all — Supabase won't say
            // which, so offer the other door rather than leaving them stuck.
            say('error', "That email and password don't match.", {
              label: 'Create a free account instead',
              run: () => { switchMode('signup'); setPassword(''); },
            });
          } else {
            say('error', error.message);
          }
        } else {
          markSignedIn();
        }
      } else if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.href },
        });
        if (error) {
          if (/already registered|already exists/i.test(error.message)) {
            alreadyRegistered();
          } else {
            say('error', error.message);
          }
        } else if (isObfuscatedExistingUser(data)) {
          alreadyRegistered();
        } else {
          setPendingEmail(email);
          setResendCooldown(RESEND_COOLDOWN_S);
        }
      } else if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: 'https://collectionkeeper.org' });
        if (error) say('error', error.message);
        else say('success', 'Password reset email sent. Check your inbox.');
      }
    } catch (e2) {
      say('error', 'Something went wrong. Try again.');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setMessage(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail,
        options: { emailRedirectTo: window.location.href },
      });
      if (error) say('error', error.message);
      else {
        say('success', 'Confirmation email sent again. Give it a minute or two.');
        setResendCooldown(RESEND_COOLDOWN_S);
      }
    } catch (e) {
      say('error', 'Could not resend. Try again.');
    }
    setLoading(false);
  };

  const handleOAuth = async (provider) => {
    setLoading(true);
    setMessage(null);

    // Native apps: Google refuses OAuth inside embedded webviews, so open the
    // system browser and let the org.collectionkeeper.app:// deep link bring
    // the session back in (handled in nativeShell.js).
    if (Capacitor.isNativePlatform()) {
      try {
        const [{ Browser }, { NATIVE_AUTH_CALLBACK }] = await Promise.all([
          import('@capacitor/browser'),
          import('./nativeShell'),
        ]);
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: { redirectTo: NATIVE_AUTH_CALLBACK, skipBrowserRedirect: true },
        });
        if (error) {
          say('error', error.message);
        } else {
          markSignedIn();
          await Browser.open({ url: data.url });
        }
      } catch (e) {
        say('error', 'Could not start sign-in. Try again.');
      }
      // Unlike web, this page stays alive under the browser sheet — release the
      // button so a collector who cancels the sheet isn't stuck on a spinner.
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          // window.location.href, not origin — otherwise signing in from a shared
          // set or gallery link drops the collector on the homepage.
          redirectTo: window.location.href,
        },
      });
      if (error) {
        say('error', error.message);
        setLoading(false);
      } else {
        markSignedIn();
      }
      // No need to setLoading(false) on success — the page will redirect to the OAuth provider
    } catch (e) {
      say('error', 'Could not start sign-in. Try again.');
      setLoading(false);
    }
  };

  const messageBlock = (
    <div style={{ ...styles.msgSlot, ...(slotReserved ? styles.msgSlotReserved : {}) }}>
      {message && (
        <div style={message.type === 'error' ? styles.msgError : styles.msgSuccess} role="alert">
          {message.text}
          {message.action && (
            <button type="button" style={styles.msgAction} onClick={message.action.run}>
              {message.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );

  // Post-signup (or unconfirmed sign-in): dedicated CHECK YOUR EMAIL panel with resend.
  if (pendingEmail) {
    return (
      <div style={styles.page}>
        <style>{CSS}</style>
        <div style={styles.stars} />
        <main style={styles.card}>
          <div style={{display:'flex',justifyContent:'center',marginBottom:'12px'}}><img src='/logo.png' width='56' height='56' style={{width:'56px',height:'56px',borderRadius:'12px'}} alt='' /></div>
          <h1 style={styles.title}>
            COLLECTION<span style={styles.titleAccent}>KEEPER</span>
          </h1>
          <div style={styles.subtitle}>// CHECK YOUR EMAIL //</div>

          <div style={styles.divider} />

          <p style={styles.checkEmailText}>
            We sent a confirmation link to
          </p>
          <div style={styles.checkEmailAddress}>{pendingEmail}</div>
          <p style={styles.checkEmailText}>
            It comes from <strong style={styles.senderStrong}>CollectionKeeper &lt;{SENDER_ADDRESS}&gt;</strong>.
            If it hasn&apos;t arrived in a couple of minutes, check your <strong>Spam</strong> or{' '}
            <strong>Promotions</strong> folder — and mark it &ldquo;Not Spam&rdquo; so the next one lands in your inbox.
          </p>

          {messageBlock}

          <button
            className="ck-btn"
            style={{ ...styles.btn, ...(resendCooldown > 0 || loading ? styles.btnDisabled : {}) }}
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
          >
            {loading ? <span style={styles.btnLoading}><i className="ck-spin" />SENDING</span> : resendCooldown > 0 ? `RESEND EMAIL (${resendCooldown}S)` : 'RESEND EMAIL'}
          </button>

          <button
            className="ck-link"
            style={styles.linkBtn}
            onClick={() => { setPendingEmail(null); switchMode('signup'); }}
          >
            Used the wrong address? Sign up again
          </button>
          <button
            className="ck-link"
            style={styles.linkBtn}
            onClick={() => { setPendingEmail(null); switchMode('login'); }}
          >
            Already confirmed? Sign in
          </button>
        </main>
      </div>
    );
  }

  const submitLabel = mode === 'login' ? 'SIGN IN' : mode === 'signup' ? 'CREATE FREE ACCOUNT' : 'SEND RESET EMAIL';
  const loadingLabel = mode === 'login' ? 'SIGNING IN' : mode === 'signup' ? 'CREATING ACCOUNT' : 'SENDING';

  return (
    <div style={styles.page}>
      <style>{CSS}</style>
      <div style={styles.stars} />
      <main style={styles.card}>
        <div style={{display:'flex',justifyContent:'center',marginBottom:'12px'}}><img src='/logo.png' width='48' height='48' style={{width:'48px',height:'48px',borderRadius:'12px'}} alt='' /></div>
        <h1 style={styles.title}>
          COLLECTION<span style={styles.titleAccent}>KEEPER</span>
        </h1>
        <div style={styles.subtitle}>// TRACK · SHARE · TRADE //</div>
        <p style={styles.valueLine}>
          Track every Topps Star Wars card you own — parallels, serials, grades and all.{' '}
          <strong style={styles.valueFree}>Free forever.</strong>
        </p>

        <div style={styles.divider} />

        {mode !== 'reset' && (
          <>
            <button
              type="button"
              className="ck-oauth"
              style={styles.googleBtn}
              onClick={() => handleOAuth('google')}
              disabled={loading}
            >
              <svg style={styles.googleIcon} viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              <span>{loading ? 'CONNECTING...' : 'CONTINUE WITH GOOGLE'}</span>
            </button>

            <button
              type="button"
              className="ck-oauth"
              style={styles.appleBtn}
              onClick={() => handleOAuth('apple')}
              disabled={loading}
            >
              <svg style={styles.appleIcon} viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              <span>{loading ? 'CONNECTING...' : 'CONTINUE WITH APPLE'}</span>
            </button>

            <div style={styles.orDivider}>
              <span style={styles.orLine} />
              <span style={styles.orText}>// OR //</span>
              <span style={styles.orLine} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {mode !== 'reset' && (
            <div style={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signup'}
                className="ck-tab"
                style={mode === 'signup' ? { ...styles.tab, ...styles.tabActive } : styles.tab}
                onClick={() => switchMode('signup')}
              >CREATE ACCOUNT</button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                className="ck-tab"
                style={mode === 'login' ? { ...styles.tab, ...styles.tabActive } : styles.tab}
                onClick={() => switchMode('login')}
              >SIGN IN</button>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label} htmlFor="ck-email">EMAIL</label>
            <input
              id="ck-email"
              name="email"
              className="ck-input"
              style={{ ...styles.input, ...(emailError ? styles.inputBad : {}) }}
              type="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              value={email}
              onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(null); }}
              onBlur={validateEmail}
              aria-invalid={emailError ? 'true' : undefined}
              aria-describedby={emailError ? 'ck-email-err' : undefined}
              placeholder="your@email.com"
            />
            {emailError && <p id="ck-email-err" style={styles.hintBad}>{emailError}</p>}
          </div>

          {mode !== 'reset' && (
            <div style={styles.field}>
              <div style={styles.labelRow}>
                <label style={styles.label} htmlFor="ck-password">PASSWORD</label>
                <button
                  type="button"
                  className="ck-toggle"
                  style={styles.toggle}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword(v => !v)}
                >{showPassword ? 'HIDE' : 'SHOW'}</button>
              </div>
              <input
                id="ck-password"
                name="password"
                className="ck-input"
                style={styles.input}
                type={showPassword ? 'text' : 'password'}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyUp={e => setCapsLock(e.getModifierState && e.getModifierState('CapsLock'))}
                onBlur={() => setCapsLock(false)}
                placeholder="••••••••"
              />
              {capsLock && <p style={styles.hintWarn}>Caps Lock is on.</p>}
              {mode === 'signup' && !capsLock && (
                <p style={styles.hint}>At least 6 characters. Not one you reuse.</p>
              )}
            </div>
          )}

          {messageBlock}

          <button type="submit" className="ck-btn" style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }} disabled={loading}>
            {loading ? <span style={styles.btnLoading}><i className="ck-spin" />{loadingLabel}</span> : submitLabel}
          </button>
        </form>

        {mode === 'login' && (
          <button className="ck-link" style={styles.linkBtn} onClick={() => switchMode('reset')}>
            Forgot password?
          </button>
        )}
        {mode === 'signup' && (
          <>
            <div style={styles.proof}>{PROOF_LINE}</div>
            <p style={styles.termsNote}>
              By creating an account you agree to our{' '}
              <a href="/terms" style={styles.termsLink}>Terms &amp; Privacy Policy</a>
            </p>
          </>
        )}
        {mode === 'reset' && (
          <button className="ck-link" style={styles.linkBtn} onClick={() => switchMode('login')}>
            Back to sign in
          </button>
        )}

        {/* === EXPLORE TILES === */}
        <div style={styles.exploreDivider} />
        <div style={styles.exploreLabel}>// EXPLORE</div>
        {/* /about is a Netlify rewrite to the static about.html — it doesn't exist
            inside the native shell's local server, so there the absolute URL routes
            the tap through the external-link interceptor into the browser sheet. */}
        <a
          href={Capacitor.isNativePlatform() ? 'https://collectionkeeper.org/about' : '/about'}
          className="ck-tile"
          style={styles.exploreAboutTile}
        >
          <div style={styles.exploreAboutTitle}>ABOUT COLLECTIONKEEPER →</div>
          <div style={styles.exploreAboutSubtitle}>The story, the platform, the promise</div>
        </a>
        <div style={styles.exploreGrid}>
          <a href="/resources" className="ck-tile" style={{...styles.exploreTile, ...styles.exploreTileCyan}}>
            <div style={styles.exploreTitle}>RESOURCES →</div>
            <div style={styles.exploreSubtitle}>Set timelines, parallel breakdowns, every card</div>
          </a>
          <a href="/deployments" className="ck-tile" style={{...styles.exploreTile, ...styles.exploreTileGold}}>
            <div style={styles.exploreTitle}>DEPLOYMENTS →</div>
            <div style={styles.exploreSubtitle}>Changelog · what we shipped recently</div>
          </a>
        </div>

        {/* === TRUST FOOTER === */}
        <div style={styles.footer}>
          <p style={styles.promise}>
            <strong style={styles.promiseStrong}>Free, ad-free, fee-free. Forever.</strong><br />
            The .org is the contract.
          </p>
          <p style={styles.dataNote}>
            Leave any time — delete your account and your collection data goes with it.
          </p>
          <p style={styles.disclaimer}>
            Not affiliated with, endorsed by, or sponsored by Lucasfilm Ltd.,
            The Walt Disney Company, or The Topps Company.
          </p>
          <a href="/terms" className="ck-legal" style={styles.legalLink}>TERMS · PRIVACY · YOUR DATA</a>
        </div>
      </main>
    </div>
  );
}

// Hover, focus and animation cannot be expressed as inline styles, so the few
// rules that need them live here rather than in the global stylesheet.
const CSS = `
.ck-oauth:hover:not(:disabled){background:#0e162c;border-color:#3a4870}
.ck-btn:hover:not(:disabled){background:#33ddff;box-shadow:0 0 18px rgba(0,212,255,.25)}
.ck-tab:hover{color:var(--text-bright)}
.ck-tab[aria-selected="true"]:hover{color:#0a1020;background:#f5cc5e}
.ck-link:hover,.ck-toggle:hover,.ck-legal:hover{color:var(--cyan)}
.ck-tile:hover{border-color:var(--border-3)}
.ck-input:focus{border-color:var(--cyan) !important}
.ck-oauth:focus-visible,.ck-btn:focus-visible,.ck-tab:focus-visible,
.ck-link:focus-visible,.ck-toggle:focus-visible,.ck-legal:focus-visible,
.ck-tile:focus-visible,.ck-input:focus-visible{
  outline:2px solid var(--cyan);outline-offset:2px;border-radius:2px;
}
.ck-spin{
  width:12px;height:12px;border:2px solid rgba(4,6,15,.25);border-top-color:#04060f;
  border-radius:50%;display:inline-block;animation:ck-sp .8s linear infinite;
}
@keyframes ck-sp{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.ck-spin{animation:none}}
`;

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'max(20px, env(safe-area-inset-top), var(--ck-android-inset-top, 0px)) max(20px, env(safe-area-inset-right)) max(20px, env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left))',
    fontFamily: "'Rajdhani', sans-serif",
    position: 'relative',
  },
  stars: {
    position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)',
    backgroundImage: `
      radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.6) 0%, transparent 100%),
      radial-gradient(1px 1px at 30% 50%, rgba(255,255,255,0.4) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 50% 10%, rgba(255,255,255,0.7) 0%, transparent 100%),
      radial-gradient(1px 1px at 70% 70%, rgba(255,255,255,0.5) 0%, transparent 100%),
      radial-gradient(1px 1px at 85% 30%, rgba(255,255,255,0.4) 0%, transparent 100%),
      radial-gradient(1px 1px at 60% 40%, rgba(255,255,255,0.5) 0%, transparent 100%),
      radial-gradient(2px 2px at 90% 60%, rgba(200,210,255,0.4) 0%, transparent 100%)
    `,
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    display: 'block',
    width: '100%',
    maxWidth: '440px',
    background: 'var(--panel-deep)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    padding: 'clamp(20px, 6vw, 40px)',
    // Halation: neon on near-black vibrates for readers with astigmatism, so the
    // glow is an accent here rather than an ambient wash.
    boxShadow: '0 0 28px rgba(var(--cyan-rgb),0.035), 0 0 120px rgba(0,0,0,0.8)',
  },
  title: {
    fontFamily: "'Orbitron', monospace",
    fontSize: 'clamp(16px, 5vw, 28px)',
    fontWeight: 900,
    textAlign: 'center',
    color: 'var(--text-strong)',
    lineHeight: 1.1,
    textShadow: '0 0 22px rgba(var(--cyan-rgb),0.16)',
    margin: 0,
  },
  titleAccent: { color: 'var(--gold)' },
  subtitle: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--muted)',
    textAlign: 'center',
    letterSpacing: '3px',
    marginTop: '6px',
  },
  valueLine: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '15px',
    lineHeight: 1.4,
    color: 'var(--text)',
    textAlign: 'center',
    margin: '10px 0 0',
  },
  valueFree: { color: 'var(--gold)', fontWeight: 600 },
  proof: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '1.2px',
    color: 'var(--muted)',
    textAlign: 'center',
    marginTop: '12px',
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, var(--border), var(--brand-gold), var(--border), transparent)',
    margin: '16px 0',
  },

  // === OAUTH BUTTONS (added v2.4.0) ===
  googleBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    padding: '13px 16px',
    minHeight: '46px',
    background: 'var(--panel)',
    border: '1px solid var(--border-3)',
    color: 'var(--text-bright)',
    cursor: 'pointer',
    borderRadius: '3px',
    marginBottom: '10px',
    transition: 'all 0.15s',
  },
  googleIcon: {
    width: '18px',
    height: '18px',
    flexShrink: 0,
  },
  appleBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    padding: '13px 16px',
    minHeight: '46px',
    background: 'var(--panel)',
    border: '1px solid var(--border-3)',
    color: 'var(--text-bright)',
    cursor: 'pointer',
    borderRadius: '3px',
    marginBottom: '10px',
    transition: 'all 0.15s',
  },
  appleIcon: {
    width: '18px',
    height: '18px',
    flexShrink: 0,
    color: 'var(--text-bright)',
  },
  orDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
  },
  orLine: {
    flex: 1,
    height: '1px',
    background: 'var(--border)',
  },
  orText: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--muted)',
    letterSpacing: '2px',
  },

  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  tab: {
    flex: 1,
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    // Tight tracking and padding keep both labels on one line down to 320px.
    // Deliberately NOT white-space:nowrap — nowrap sets a min-content floor the
    // flex item cannot shrink below, which overflows the viewport (WCAG 1.4.10).
    // Wrapping is the acceptable failure here; horizontal scroll is not.
    letterSpacing: '0.5px',
    padding: '13px 4px',
    minHeight: '44px',
    // Longhand, not the `border` shorthand: tabActive overrides borderColor, and
    // React blanks the decomposed longhands on switch-back if the two are mixed,
    // which drops the inactive tab's hairline to black.
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--border)',
    background: 'transparent',
    color: 'var(--muted)',
    cursor: 'pointer',
    borderRadius: '2px',
    transition: 'all 0.15s',
  },
  // Solid fill on the selected tab: two outlined boxes of the same shape read as
  // two equal options, which is how collectors ended up in the wrong mode.
  tabActive: {
    borderColor: 'var(--gold)',
    background: 'var(--gold)',
    color: '#0a1020',
    fontWeight: 700,
  },
  field: { marginBottom: '12px' },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  label: {
    display: 'block',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: 'var(--muted)',
    marginBottom: '6px',
  },
  toggle: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '1px',
    color: 'var(--cyan)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    minWidth: '44px',
    minHeight: '44px',
    padding: '0 6px',
    margin: '-14px -6px',
  },
  input: {
    width: '100%',
    background: 'var(--panel)',
    // Longhand for the same reason as .tab — inputBad overrides borderColor.
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--border-3)',
    color: 'var(--text)',
    fontFamily: "'Share Tech Mono', monospace",
    // 16px keeps iOS from zooming the page when the field takes focus.
    fontSize: '16px',
    padding: '13px 14px',
    borderRadius: '2px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputBad: { borderColor: 'var(--red)' },
  hint: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '12.5px',
    color: 'var(--muted)',
    lineHeight: 1.4,
    margin: '7px 0 0',
  },
  hintWarn: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '12.5px',
    color: 'var(--gold)',
    lineHeight: 1.4,
    margin: '7px 0 0',
  },
  hintBad: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '12.5px',
    color: '#ff8fa3',
    lineHeight: 1.4,
    margin: '7px 0 0',
  },
  btn: {
    width: '100%',
    fontFamily: "'Orbitron', monospace",
    fontSize: '12px',
    letterSpacing: '2px',
    fontWeight: 700,
    padding: '15px',
    minHeight: '48px',
    background: 'var(--cyan)',
    border: '1px solid var(--cyan)',
    color: '#04060f',
    cursor: 'pointer',
    borderRadius: '2px',
    marginTop: '6px',
    transition: 'all 0.2s',
  },
  btnDisabled: {
    opacity: 0.45,
    cursor: 'default',
  },
  btnLoading: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  checkEmailText: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '14px',
    color: 'var(--text)',
    lineHeight: 1.6,
    textAlign: 'center',
    margin: '0 0 12px',
  },
  checkEmailAddress: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '14px',
    color: 'var(--cyan)',
    textAlign: 'center',
    padding: '10px 14px',
    background: 'var(--panel)',
    border: '1px solid var(--border)',
    borderRadius: '2px',
    marginBottom: '14px',
    wordBreak: 'break-all',
  },
  senderStrong: {
    color: 'var(--text-strong)',
  },
  linkBtn: {
    display: 'block',
    width: '100%',
    background: 'none',
    border: 'none',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '12px',
    color: 'var(--muted)',
    cursor: 'pointer',
    textAlign: 'center',
    marginTop: '10px',
    padding: '13px',
    minHeight: '44px',
  },
  // Once a message has appeared the slot stays reserved, so the next one does not
  // shove the submit button down mid-tap.
  msgSlot: {},
  msgSlotReserved: { minHeight: '46px' },
  msgError: {
    background: 'rgba(var(--red-rgb),0.1)',
    border: '1px solid rgba(var(--red-rgb),0.3)',
    color: '#ff8fa3',
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '13.5px',
    padding: '10px 14px',
    borderRadius: '2px',
    marginBottom: '12px',
    lineHeight: 1.45,
  },
  msgSuccess: {
    background: 'rgba(var(--green-rgb),0.08)',
    border: '1px solid rgba(var(--green-rgb),0.3)',
    color: 'var(--green)',
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '13.5px',
    padding: '10px 14px',
    borderRadius: '2px',
    marginBottom: '12px',
    lineHeight: 1.45,
  },
  msgAction: {
    // inline-flex + min-height rather than block: this sits on its own line, so
    // it gets no inline-target exemption from 2.5.8 and needs the full 44px.
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '44px',
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '13.5px',
    color: 'var(--cyan)',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  termsNote: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--muted)',
    textAlign: 'center',
    marginTop: '10px',
    lineHeight: 1.6,
  },
  termsLink: {
    color: 'var(--cyan)',
    textDecoration: 'none',
  },

  // === EXPLORE TILES (added v2.2.2) ===
  exploreDivider: {
    height: '1px',
    background: 'var(--panel-2)',
    margin: '24px 0 16px',
  },
  exploreLabel: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    letterSpacing: '3px',
    color: 'var(--muted)',
    textAlign: 'center',
    marginBottom: '12px',
  },
  // Full-width primary ABOUT tile (added v2.3.0). Left accent rather than a full
  // cyan border, so the submit button is the only thing wearing solid cyan.
  exploreAboutTile: {
    display: 'block',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(var(--cyan-rgb),0.05) 0%, rgba(var(--gold-rgb),0.02) 100%)',
    border: '1px solid var(--border)',
    borderLeft: '2px solid var(--cyan)',
    borderRadius: '0 3px 3px 0',
    textDecoration: 'none',
    marginBottom: '8px',
    transition: 'all 0.15s ease',
  },
  exploreAboutTitle: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: 'var(--text-bright)',
    marginBottom: '4px',
  },
  exploreAboutSubtitle: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '12px',
    color: 'var(--muted-8)',
    lineHeight: 1.4,
  },
  exploreGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  },
  exploreTile: {
    display: 'block',
    padding: '12px 14px',
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: '0 3px 3px 0',
    textDecoration: 'none',
    transition: 'all 0.15s ease',
    minWidth: 0,
  },
  exploreTileCyan: {
    borderLeft: '2px solid var(--cyan)',
  },
  exploreTileGold: {
    borderLeft: '2px solid var(--gold)',
  },
  exploreTitle: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: 'var(--text-bright)',
    marginBottom: '4px',
  },
  exploreSubtitle: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '11.5px',
    color: 'var(--muted)',
    lineHeight: 1.4,
  },

  // === TRUST FOOTER ===
  footer: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid var(--panel-2)',
    textAlign: 'center',
  },
  promise: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '12.5px',
    color: 'var(--muted-8)',
    lineHeight: 1.5,
    margin: 0,
  },
  promiseStrong: { color: 'var(--gold)', fontWeight: 600 },
  dataNote: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '12.5px',
    color: 'var(--muted-8)',
    lineHeight: 1.5,
    margin: '10px 0 0',
  },
  disclaimer: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '11.5px',
    color: 'var(--muted)',
    lineHeight: 1.45,
    margin: '10px 0 0',
  },
  legalLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '6px',
    minHeight: '44px',
    padding: '0 12px',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: 'var(--muted)',
    textDecoration: 'none',
  },
};
